import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle2, 
  BarChart3,
  Filter,
  Download,
  Plus,
  Eye,
  BookOpen,
  ChevronRight,
  MoreVertical,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import SettingsHeader from '../../ui/SettingsHeader';
import Loader from '../../ui/Loader';

const AttendanceDashboard = ({ availableClasses = [], onClassSelect, onTakeAttendance }) => {
  const { attendance: apiAttendance, students: apiStudents, statistics, loadAttendance, loading } = useData();
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const selectedClassForSections = availableClasses.find(c => c._id === selectedClass);

  useEffect(() => {
    loadAttendance({ date: selectedDate, unitId: selectedClass, sectionId: selectedSection });
  }, [selectedDate, selectedClass, selectedSection, loadAttendance]);

  // Enhanced statistics handling with backend priority
  const stats = (() => {
    if (statistics?.attendance) {
      return {
        totalStudents: statistics.attendance.totalStudents || 0,
        presentToday: statistics.attendance.presentCount || statistics.attendance.presentToday || 0,
        absentToday: statistics.attendance.absentCount || statistics.attendance.absentToday || 0,
        attendanceRate: statistics.attendance.attendanceRate || 0
      };
    }
    
    // Legacy fallback calculation
    const currentClassStudents = selectedClass 
      ? apiStudents.filter(s => (s.enrollment?.unitId?._id || s.enrollment?.unitId || s.unitId) === selectedClass)
      : apiStudents;
    
    const totalCount = currentClassStudents.length;
    const todayStr = selectedDate;
    
    const records = apiAttendance.filter(att => {
      try {
        return new Date(att.date).toISOString().split('T')[0] === todayStr &&
               (selectedClass ? att.unitId === selectedClass : true);
      } catch (e) { return false; }
    });

    const presentCount = records.filter(a => a.status === 'present').length;
    const absentCount = records.filter(a => a.status === 'absent').length;
    const rate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

    return {
      totalStudents: totalCount,
      presentToday: presentCount,
      absentToday: absentCount,
      attendanceRate: rate
    };
  })();

  const recentUpdates = apiAttendance
    .filter(att => att.date && (selectedClass ? att.unitId === selectedClass : true))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5)
    .map(att => ({
      ...att,
      className: availableClasses.find(c => c._id === att.unitId)?.unitName || 'Class',
      time: new Date(att.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));

  if (loading.attendance) {
    return <Loader />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <SettingsHeader
        title="Attendance Overview"
        description={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        showBack={false}
        icon={BarChart3}
        buttons={[
          {
            label: "Mark Attendance",
            icon: Plus,
            onClick: onTakeAttendance,
            variant: "default",
          },
        ]}
      >
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Class Selector */}
          <div className="relative min-w-[140px]">
            <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedSection(''); // Reset section when class changes
              }}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg outline-none input-field transition-all appearance-none cursor-pointer hover:bg-white"
            >
              <option value="">All Classes</option>
              {availableClasses.map(cls => (
                <option key={cls._id} value={cls._id}>{cls.unitName}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
              <ChevronRight className="w-3.5 h-3.5 rotate-90" />
            </div>
          </div>

          {/* Section Selector (Conditional) */}
          {selectedClassForSections?.sections?.length > 0 && (
            <div className="relative min-w-[120px] animate-in fade-in slide-in-from-left-2 duration-300">
              <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg outline-none input-field transition-all appearance-none cursor-pointer hover:bg-white"
              >
                <option value="">All Sections</option>
                {selectedClassForSections.sections.map(sec => (
                  <option key={sec._id} value={sec._id}>{sec.name}</option>
                ))}
              </select>
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <ChevronRight className="w-3.5 h-3.5 rotate-90" />
              </div>
            </div>
          )}

          <div className="relative min-w-[130px]">
            <Calendar className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50/50 border border-gray-200 rounded-lg outline-none input-field transition-all hover:bg-white"
            />
          </div>
        </div>
      </SettingsHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard 
          title="Total Students" 
          value={stats.totalStudents} 
          icon={Users} 
          trend="Total"
          color="indigo"
          description="Enrolled"
        />
        <KpiCard 
          title="Present Today" 
          value={stats.presentToday} 
          icon={CheckCircle2} 
          trend={`${stats.attendanceRate}%`}
          color="emerald" 
          description="In Session"
        />
        <KpiCard 
          title="Absent Today" 
          value={stats.absentToday} 
          icon={AlertTriangle} 
          trend={`${100 - stats.attendanceRate}%`}
          color="rose"
          description="Inactive"
        />
        <KpiCard 
          title="Attendance Rate" 
          value={`${stats.attendanceRate}%`} 
          icon={TrendingUp} 
          trend="Avg"
          color="amber"
          description="Efficiency"
        />
      </div>


    </div>
  );
};

const KpiCard = ({ title, value, icon: Icon, color, trend, description }) => {
    const colorMap = {
        indigo: 'text-indigo-600 bg-indigo-50 border-indigo-100',
        emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
        rose: 'text-rose-600 bg-rose-50 border-rose-100',
        amber: 'text-amber-600 bg-amber-50 border-amber-100',
    };

    return (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col gap-3 group hover:border-indigo-100 transition-all duration-300">
            <div className="flex justify-between items-center">
                <div className={`p-1.5 rounded border ${colorMap[color] || 'bg-gray-50'}`}>
                    <Icon size={12} />
                </div>
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50/50 rounded-full border border-gray-100">
                    <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">{trend}</span>
                </div>
            </div>
            <div>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{title}</p>
                <div className="flex items-baseline gap-2 mt-1">
                    <h3 className="text-3xl font-black text-gray-900 leading-none">{value}</h3>
                    <span className="text-[9px] font-medium text-gray-300 lowercase">{description}</span>
                </div>
            </div>
        </div>
    );
};

export default AttendanceDashboard;